import axios from 'axios';
import { getToken } from './auth';
import { Task } from '../types/task';

const API_BASE_URL = '/.netlify/functions';

export interface TaskFilters {
  stage?: string;
  status?: string;
  priority?: string;
  assignedTo?: string;
  serviceType?: string;
  search?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  tasks: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TaskCounts {
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  overdue: number;
}

export interface LazyTasksResponse {
  shipmentId: string;
  tasks: Task[];
  count: number;
}

export interface TaskCountsResponse {
  counts: Record<string, TaskCounts>;
}

/**
 * Lazy load tasks for a specific shipment
 * This is the optimized way to load tasks - only when user expands a shipment
 */
export const getTasksForShipmentLazy = async (
  shipmentId: string, 
  filters?: { status?: string; priority?: string; stage?: string }
): Promise<LazyTasksResponse> => {
  const token = getToken();
  const params = new URLSearchParams();
  params.append('shipmentId', shipmentId);
  
  if (filters?.status) params.append('status', filters.status);
  if (filters?.priority) params.append('priority', filters.priority);
  if (filters?.stage) params.append('stage', filters.stage);
  
  const response = await axios.get(`${API_BASE_URL}/tasks-by-shipment?${params}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

/**
 * Get task counts for multiple shipments efficiently
 * Uses MongoDB aggregation - much faster than loading all tasks
 */
export const getTaskCountsForShipments = async (shipmentIds: string[]): Promise<TaskCountsResponse> => {
  if (shipmentIds.length === 0) {
    return { counts: {} };
  }
  
  const token = getToken();
  const params = new URLSearchParams();
  params.append('operation', 'counts');
  params.append('shipmentIds', shipmentIds.join(','));
  
  const response = await axios.get(`${API_BASE_URL}/tasks-by-shipment?${params}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

export interface TaskGenerationRequest {
  shipmentId: string;
  shipmentName: string;
  services: string[];
  customTasks?: Array<{
    title: string;
    description?: string;
    stage: string;
    priority: string;
    serviceType: string;
  }>;
}

export interface TaskUpdateRequest {
  taskId: string;
  updates: Partial<{
    title: string;
    description: string;
    stage: string;
    status: string;
    priority: string;
    assignedTo: string;
    assignedToName: string;
    dueDate: Date;
    notes: string;
  }>;
}

export interface TaskAssignmentRequest {
  taskId: string;
  assignedTo: string;
  assignedToName: string;
  assignedBy: string;
  notes?: string;
}

export interface TaskCompletionRequest {
  taskId: string;
  completedBy: string;
  completedByName: string;
}

// Task CRUD operations
export const getTasks = async (filters?: TaskFilters, pagination?: PaginationParams): Promise<Task[] | PaginatedResponse<Task>> => {
  const token = getToken();
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
  }
  
  // Add pagination params if provided
  if (pagination) {
    if (pagination.page !== undefined) params.append('page', pagination.page.toString());
    if (pagination.limit !== undefined) params.append('limit', pagination.limit.toString());
  }
  
  // Always add operation=tasks when no specific operation is specified
  if (!filters || Object.keys(filters).length === 0) {
    params.append('operation', 'tasks');
  }
  
  const response = await axios.get(`${API_BASE_URL}/tasks?${params}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

export const getTaskDashboard = async () => {
  const token = getToken();
  const response = await axios.get(`${API_BASE_URL}/tasks?operation=dashboard`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

// RoRo and container shipments each have their own editable template set.
export const getTaskTemplates = async (method: 'roro' | 'container' = 'roro') => {
  const token = getToken();
  const response = await axios.get(`${API_BASE_URL}/tasks?operation=templates&method=${method}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

export const updateTaskTemplates = async (templates: any, method: 'roro' | 'container' = 'roro') => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/tasks`, 
    { templates, method, operation: 'update-templates' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const getTasksByShipment = async (shipmentId: string, pagination?: PaginationParams): Promise<Task[] | PaginatedResponse<Task>> => {
  const token = getToken();
  const params = new URLSearchParams();
  params.append('shipmentId', shipmentId);
  
  // Add pagination params if provided
  if (pagination) {
    if (pagination.page !== undefined) params.append('page', pagination.page.toString());
    if (pagination.limit !== undefined) params.append('limit', pagination.limit.toString());
  }
  
  const response = await axios.get(`${API_BASE_URL}/tasks?${params}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

export const getTasksByUser = async (userId: string, pagination?: PaginationParams): Promise<Task[] | PaginatedResponse<Task>> => {
  const token = getToken();
  const params = new URLSearchParams();
  params.append('operation', 'user');
  params.append('userId', userId);
  
  // Add pagination params if provided
  if (pagination) {
    if (pagination.page !== undefined) params.append('page', pagination.page.toString());
    if (pagination.limit !== undefined) params.append('limit', pagination.limit.toString());
  }
  
  const response = await axios.get(`${API_BASE_URL}/tasks?${params}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
  return response.data;
};

export const getAllTasks = async (pagination?: PaginationParams): Promise<Task[] | PaginatedResponse<Task>> => {
  const token = getToken();
  const params = new URLSearchParams();
  params.append('operation', 'all');
  
  // Add pagination params if provided
  if (pagination) {
    if (pagination.page !== undefined) params.append('page', pagination.page.toString());
    if (pagination.limit !== undefined) params.append('limit', pagination.limit.toString());
  }
  
  const response = await axios.get(
    `${API_BASE_URL}/tasks?${params}`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const generateTasks = async (request: TaskGenerationRequest) => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'generate' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const createTask = async (taskData: any) => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/tasks`, 
    { ...taskData, operation: 'create' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const updateTask = async (request: TaskUpdateRequest) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'update' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const assignTask = async (request: TaskAssignmentRequest) => {
  console.log('assignTask service called with:', request);
  const token = getToken();
  const requestBody = { ...request, operation: 'assign' };
  console.log('Sending to API:', requestBody);
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    requestBody,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const completeTask = async (request: TaskCompletionRequest) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'complete' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const deleteTask = async (taskId: string) => {
  console.log('deleteTask service called with taskId:', taskId);
  const token = getToken();
  const requestBody = { taskId, operation: 'delete' };
  console.log('Sending to API:', requestBody);
  const response = await axios.delete(
    `${API_BASE_URL}/tasks`, 
    {
      data: requestBody,
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export interface BatchCompleteRequest {
  completedBy: string;
  completedByName: string;
  shipmentId?: string;
  stage?: string;
  status?: string; // Optional filter by current status (e.g., 'in_progress')
}

export const completeBatchTasks = async (request: BatchCompleteRequest) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'batch-complete' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export interface BatchIncompleteRequest {
  shipmentId?: string;
  stage?: string;
  status?: string; // Optional filter by current status (e.g., 'completed')
}

export const incompleteBatchTasks = async (request: BatchIncompleteRequest) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'batch-incomplete' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export interface BatchAssignShipmentRequest {
  shipmentId: string;
  assignedTo: string;
  assignedToName: string;
}

export const batchAssignShipmentTasks = async (request: BatchAssignShipmentRequest) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'batch-assign-shipment' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export interface BatchUnassignShipmentRequest {
  shipmentId: string;
}

export const batchUnassignShipmentTasks = async (request: BatchUnassignShipmentRequest) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/tasks`, 
    { ...request, operation: 'batch-unassign-shipment' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

// Notification operations
export const getNotifications = async (userId: string, limit = 50, unreadOnly = false) => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/notifications?userId=${userId}&limit=${limit}&unreadOnly=${unreadOnly}`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const getNotificationDashboard = async (userId: string) => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/notifications?userId=${userId}&operation=dashboard`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const getShipmentUpdates = async (shipmentId: string, limit = 20) => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/notifications?shipmentId=${shipmentId}&limit=${limit}&operation=shipment-updates`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const createNotification = async (notificationData: any) => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/notifications`, 
    { ...notificationData, operation: 'create' },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const markNotificationsAsRead = async (notificationIds: string[], userId: string) => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/notifications`,
    {
      notificationIds,
      userId,
      operation: 'mark-read'
    },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const updateNotification = async (notificationId: string, updates: any) => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/notifications`,
    {
      notificationId,
      updates,
      operation: 'update'
    },
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};

export const deleteNotification = async (notificationId: string, userId: string) => {
  const token = getToken();
  const response = await axios.delete(
    `${API_BASE_URL}/notifications`,
    {
      data: { notificationId, userId, operation: 'delete' },
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  );
  return response.data;
};
